<template>
  <div class="template-container">
    <div
      class="content-container"
      :class="{ 'sidebar-displayed': selectedLicense || showRenewSidebar }"
    >
      <div class="container-header">
        <div class="search-button-container">
          <SearchButton
            v-model="licensesSearchText"
            @blur="showLicensesSearchBox = !!licensesSearchText"
            @input="onDebouncedSearchChanged"
          />
        </div>
        <button
          class="button renew-button"
          :class="{ 'text-red': renewSelected }"
          title="Renew Licenses"
          @click="openRenewSidebar"
          v-if="isAdmin"
        >
          <span class="material-symbols-outlined"> autorenew </span>Renew License
        </button>
        <button class="button add-button" @click="openCreateLicenseModal" v-if="isAdmin">
          <span class="material-symbols-outlined"> add </span> Add License
        </button>
      </div>
      <div class="table-container">
        <LicensesList
          ref="LicensesList"
          :licenses="licenses"
          :renewMode="renewSelected"
          @licenseSelected="setSelectedLicenses"
          @onDelete="getLicenses(true)"
        />
      </div>
    </div>

    <CreateLicenseModal
      v-if="showCreateLicenseModal"
      :organisation-id="organisationIdParam"
      @onCreated="getLicenses"
      @closeModal="showCreateLicenseModal = false"
    />

    <EditLicensesSidebar
      v-if="selectedLicense"
      :license="selectedLicense"
      @licenseUpdated="getLicenses(true)"
      @licenseDeleted="getLicenses(true)"
    />

    <RenewLicensesSidebar
      v-if="renewSelected"
      :selectedLicenses="selectedLicenses"
      @licensesUpdated="getLicenses"
      @closeSidebar="closeRenewSidebar"
      @removeLicense="removeSelectedLicense"
    />
  </div>
</template>

<script>
  import LicensesList from '@/components/admin/OrganisationDetails/Licenses/LicensesList.vue';
  import EditLicensesSidebar from '@/components/admin/OrganisationDetails/Licenses/EditLicenseSidebar.vue';
  import RenewLicensesSidebar from '@/components/admin/OrganisationDetails/Licenses/RenewLicensesSidebar.vue';
  import { getOrganisationLicenses, getLicenses } from '@/api/organisation';
  import * as ORGANISATION from '@/store/actions/organisation';
  import CreateLicenseModal from '@/components/admin/OrganisationDetails/Licenses/CreateLicenseModal.vue';
  import { debounce } from 'lodash';
  import { ADMIN_ORGANISATIONS_GET } from '@/store/actions/organisation';
  import SearchButton from '@/components/common/SearchButton.vue';

  export default {
    name: 'Licenses',

    components: {
      CreateLicenseModal,
      LicensesList,
      RenewLicensesSidebar,
      EditLicensesSidebar,
      SearchButton,
    },

    data() {
      return {
        showCreateLicenseModal: false,
        showLicensesFilters: false,
        showLicensesSearchBox: false,
        licensesSearchText: '',
        renewSelected: false,
        selectedLicense: null,
        selectedLicenses: [],
        licenses: [],
        filteredStatus: 'all',
        showRenewSidebar: false,
      };
    },

    computed: {
      isOrganizationAdmin() {
        return this.$store.getters.isOrganizationAdmin;
      },

      isAdmin() {
        return this.$store.getters.isAdministrator;
      },

      organisation() {
        return this.$store.state.organisation.organisation;
      },

      organisationIdParam() {
        return this.$route.params.organisation_id;
      },
    },

    created() {
      this.getLicenses();
    },

    methods: {
      async fetchLicenses(data) {
        if (this.isAdmin) {
          const res = await getOrganisationLicenses(this.organisationIdParam, data);
          return res.data;
        } else {
          const res = await getLicenses(data);
          return res.data;
        }
      },

      async getLicenses(refresh = false) {
        if (!refresh) {
          this.selectedLicense = null;
        }

        if (!this.organisation) {
          if (this.isAdmin) {
            await this.$store.dispatch(ADMIN_ORGANISATIONS_GET, this.organisationIdParam);
          } else {
            await this.$store.dispatch(ORGANISATION.ORGANISATION_REQUEST);
          }
        }

        try {
          const filteredData = {};

          if (this.licensesSearchText) {
            filteredData['name'] = this.licensesSearchText;
          }
          this.licenses = await this.fetchLicenses(filteredData);
        } catch {
          this.$toasted.global.general_error({ message: 'Failed to load licenses' });
        }
      },

      setSelectedLicenses(data) {
        if (this.renewSelected) {
          this.selectedLicenses = data;
          return;
        }

        if (this.selectedLicense && this.selectedLicense.id === data.id) {
          this.selectedLicense = null;
        } else {
          this.selectedLicense = data;
        }
      },

      openRenewSidebar() {
        this.selectedLicense = null;
        this.selectedLicenses = [];
        this.renewSelected = !this.renewSelected;
      },

      closeRenewSidebar() {
        this.selectedLicenses = [];
        this.selectedLicense = null;
        this.renewSelected = false;
      },

      openCreateLicenseModal() {
        this.showCreateLicenseModal = true;
      },

      licensesFilterIconClick() {
        this.showLicensesFilters = !this.showLicensesFilters;
      },

      licensesSearchIconClick() {
        this.showLicensesSearchBox = !this.showLicensesSearchBox;
      },
      removeSelectedLicense(license) {
        this.$refs.LicensesList.setSelectedLicense(license);
      },
      onDebouncedSearchChanged: debounce(function (e) {
        const value = e;

        if (!value || (value && value.length >= 3)) {
          this.getLicenses();
        }
      }, 700),
    },
  };
</script>

<style scoped lang="scss">
  .template-container {
    display: flex;
    width: 100%;
    height: 100%;
  }

  .content-container {
    width: 100%;
    height: 100%;

    &.sidebar-displayed {
      width: 80%;
    }
  }

  .table-container {
    overflow-y: auto;
    overflow-x: visible;
    max-height: 90%;
    width: 100%;
    padding-right: 12px;
  }

  table {
    background-color: $backgroundGrey;
    width: 100%;
    font-size: 14px;
    border: none;
    border-spacing: 0 8px;
    border-collapse: separate;
    table-layout: fixed;
  }

  thead {
    background-color: $backgroundGrey;
    height: 50px;
    position: sticky;
    top: 0;
    z-index: 2;

    th {
      padding-left: 15px;
      padding-right: 15px;
    }

    th.name {
      width: 25%;
    }

    th.expiry-date {
      width: 16%;
    }

    th.status {
      width: 10%;
    }

    th.start-date {
      width: 16%;
    }

    th.screen {
      width: 10%;
    }

    th.creator {
      width: 20%;
    }

    th.delete {
      width: 3%;
    }
  }
  tbody {
    display: block;
    max-height: calc(100vh - 50px);
    overflow-y: auto;

    tr {
      display: table;
      width: 100%;
      table-layout: fixed;
      background-color: #fff;
      cursor: pointer;

      font-size: 14px;

      td {
        padding: 15px;

        .dropdown {
          display: flex;
          justify-content: flex-end;
        }

        border-top: 2px solid transparent;
        border-bottom: 2px solid transparent;

        overflow-wrap: break-word;

        &:first-child {
          border-left: 2px solid transparent;
          font-weight: bold;
          width: 20%;
          // border-radius: 4px 0px 0px 4px;
        }

        &:last-child {
          border-right: 2px solid transparent;
          // border-radius: 0px 4px 4px 0px;
        }
      }

      &:hover,
      &.selected {
        td {
          border-top: 2px solid $primaryRed;
          border-bottom: 2px solid $primaryRed;

          &:first-child {
            border-left: 2px solid $primaryRed;
          }

          &:last-child {
            border-right: 2px solid $primaryRed;
          }
        }
      }
    }
  }

  tr.near-expiration {
    background-color: #ffdde6;
    border-color: #ffdde6;
  }
  td.near-expiration {
    font-weight: bold;
  }

  .sidebar-container {
    display: none;
    height: 100%;

    :active {
      display: block;
    }
  }

  .container-header {
    display: grid;
    grid-template-columns: auto 160px 160px;

    margin: 10px;

    .button {
      border: none;
      background-color: transparent;
      font-size: 16px;
      font-weight: bold;
      display: flex;
      align-items: center;
      height: 40px;
      justify-content: space-evenly;
    }
  }

  .add-button {
    color: $primaryRed;
  }

  .renew-button {
    color: $secondaryText;
  }

  .search-button-container {
    display: flex;
    align-items: center;
  }
</style>
